import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { DEFAULT_LANG, type Lang } from "@/i18n/translations";

const STORAGE_KEY = "pps:lang-banner-dismissed";

const detectBrowserLang = (): Lang | null => {
  if (typeof navigator === "undefined") return null;
  const langs = (navigator.languages?.length ? navigator.languages : [navigator.language]) || [];
  for (const raw of langs) {
    const code = raw.toLowerCase().split("-")[0];
    if (code === "es") return "es";
    if (code === "pt") return "pt";
    if (code === "en") return "en";
  }
  return null;
};

const LanguageBanner = () => {
  const { lang, switchTo, t } = useLang();
  const [suggest, setSuggest] = useState<Lang | null>(null);

  useEffect(() => {
    if (lang !== DEFAULT_LANG) return; // already on a non-default language
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
      if (localStorage.getItem("pps:lang")) return; // user has explicitly chosen before
    } catch {
      /* ignore */
    }
    const detected = detectBrowserLang();
    if (detected && detected !== DEFAULT_LANG) {
      setSuggest(detected);
    }
  }, [lang]);

  if (!suggest) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setSuggest(null);
  };

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    switchTo(suggest);
    setSuggest(null);
  };

  return (
    <div
      role="region"
      aria-label="Language suggestion"
      className="sticky top-16 z-40 mx-auto w-full border-b border-border bg-secondary/95 backdrop-blur md:top-32"
    >
      <div className="container flex items-center justify-between gap-3 py-2">
        <p className="text-sm font-medium text-foreground">
          {suggest === "es" ? t("banner.es") : t("banner.pt")}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:bg-foreground/90"
          >
            {t("banner.switch")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("banner.dismiss")}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageBanner;
